import { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import BackButton from './BackButton.js';
import NavBar from './nav_bar.js';
import useBfcacheReset from './bfcache.js';
import SupportLine from './support_line.js';
import './index.css';

// /tickets/:artist — a clickable poster that starts a Stripe Checkout for one
// ticket (on return via ?session_id=... it polls until the ticket is issued,
// then shows the QR, also emailed), plus a "request a song for this artist to
// perform" form that starts its own paid Checkout (?song_session_id=...).
function TicketPage() {
  const { artist } = useParams();
  const [search] = useSearchParams();
  const sessionId = search.get('session_id');
  const songSessionId = search.get('song_session_id');

  const [info, setInfo] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');
  const [ticket, setTicket] = useState(null);
  const [finalizing, setFinalizing] = useState(!!sessionId);

  // song request
  const [songForm, setSongForm] = useState({ title: '', originalArtist: '', link: '' });
  const [submittingSong, setSubmittingSong] = useState(false);
  const [songError, setSongError] = useState('');
  const [songResult, setSongResult] = useState(null);
  const [finalizingSong, setFinalizingSong] = useState(!!songSessionId);

  // artist / poster metadata
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/tickets/${encodeURIComponent(artist)}/info`);
        if (res.status === 404) { if (!cancelled) setNotFound(true); return; }
        const data = await res.json();
        if (!cancelled) setInfo(data);
      } catch {
        if (!cancelled) setError('Could not load this show.');
      }
    })();
    return () => { cancelled = true; };
  }, [artist]);

  // after ticket checkout: poll for the issued ticket (webhook may lag redirect)
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const res = await fetch(`/tickets/session/${encodeURIComponent(sessionId)}`);
        if (res.ok) {
          const data = await res.json();
          if (!cancelled) { setTicket(data); setFinalizing(false); }
          return;
        }
        if (res.status === 202 && tries < 12 && !cancelled) { setTimeout(poll, 1500); return; }
        if (!cancelled) {
          setFinalizing(false);
          setError('Payment received — your ticket is on its way by email.');
        }
      } catch {
        if (tries < 12 && !cancelled) { setTimeout(poll, 1500); return; }
        if (!cancelled) {
          setFinalizing(false);
          setError('Payment received — check your email for your ticket.');
        }
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  // after song-request checkout: poll for the recorded request
  useEffect(() => {
    if (!songSessionId) return;
    let cancelled = false;
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const res = await fetch(
          `/tickets/${encodeURIComponent(artist)}/song/session/${encodeURIComponent(songSessionId)}`
        );
        if (res.ok) {
          const data = await res.json();
          if (!cancelled) { setSongResult(data); setFinalizingSong(false); }
          return;
        }
        if (res.status === 202 && tries < 12 && !cancelled) { setTimeout(poll, 1500); return; }
        if (!cancelled) {
          setFinalizingSong(false);
          setSongError('Payment received — your request is being recorded. Check your email.');
        }
      } catch {
        if (tries < 12 && !cancelled) { setTimeout(poll, 1500); return; }
        if (!cancelled) {
          setFinalizingSong(false);
          setSongError('Payment received — check your email.');
        }
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [songSessionId, artist]);

  // Back button after a Stripe redirect restores this page frozen — clear the
  // stuck "Opening checkout…" state on both the ticket button and song form.
  useBfcacheReset(() => { setStarting(false); setSubmittingSong(false); }, starting || submittingSong);

  const buy = useCallback(async () => {
    setStarting(true);
    setError('');
    try {
      const res = await fetch(`/tickets/${encodeURIComponent(artist)}/checkout`, { method: 'POST' });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.url) throw new Error(data?.msg || 'Could not start checkout.');
      window.location.href = data.url;
    } catch (err) {
      setError(err.message);
      setStarting(false);
    }
  }, [artist]);

  const submitSongRequest = useCallback(async (e) => {
    e.preventDefault();
    setSubmittingSong(true);
    setSongError('');
    try {
      const res = await fetch(`/tickets/${encodeURIComponent(artist)}/song/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: songForm.title.trim(),
          originalArtist: songForm.originalArtist.trim(),
          link: songForm.link.trim(),
        }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.url) throw new Error(data?.msg || 'Could not start checkout.');
      window.location.href = data.url;
    } catch (err) {
      setSongError(err.message);
      setSubmittingSong(false);
    }
  }, [artist, songForm]);

  if (notFound) {
    return (
      <>
        <NavBar />
        <main className='page'>
          <div className='page-content'>
            <BackButton fallback='/' />
            <div className='card'>
              <h1>Show not found</h1>
              <p>There's no ticket page for "{artist}".</p>
            </div>
          </div>
        </main>
      </>
    );
  }

  const priceLabel = info?.songPriceDisplay ? ` — ${info.songPriceDisplay}` : '';
  const hasSongInput = !!(songForm.title.trim() || songForm.originalArtist.trim() || songForm.link.trim());

  return (
    <>
      <NavBar />
      <main className='page'>
      <div className='page-content'>
        <BackButton fallback='/' />

        {songResult ? (
          <div className='card ticket-card'>
            <h1>{songResult.hasSong ? 'Song request received' : "You're in"}</h1>
            {songResult.entryCode && (
              <p className='entry-code'>{songResult.entryCode}<span> entry code</span></p>
            )}
            {(songResult.date || info?.date) && (
              <p className='event-date'>{songResult.date || info.date}</p>
            )}
            {(songResult.address || info?.address) && (
              <p className='event-address'>{songResult.address || info.address}</p>
            )}
            {songResult.hasSong ? (
              <p>
                Your request for <strong>{songResult.title}</strong>
                {songResult.originalArtist ? ` by ${songResult.originalArtist}` : ''} is in for{' '}
                {songResult.artistName}. The most-requested song gets played — no guarantees on any
                single request.
              </p>
            ) : (
              <p>Your contribution to {songResult.artistName}'s set is in.</p>
            )}
            <p className='perk-line'>
              <strong>Admission included.</strong>{' '}
              {songResult.freeShotNote || '21+ get a free shot at the bar — bring valid ID.'}
            </p>
            {songResult.qr && <img className='ticket-qr' src={songResult.qr} alt='Your entry QR code' />}
            <p className='help-text'>Show this QR at the door — one entry.</p>
            {songResult.email && <p className='help-text'>A receipt was emailed to {songResult.email}.</p>}
          </div>
        ) : finalizingSong ? (
          <div className='card'>
            <h1>Finalizing your song request…</h1>
            <p className='help-text'>One moment — confirming your payment.</p>
          </div>
        ) : ticket ? (
          <div className='card ticket-card'>
            <h1>You're in for {ticket.artistName || 'the show'}</h1>
            {ticket.entryCode && (
              <p className='entry-code'>{ticket.entryCode}<span> entry code</span></p>
            )}
            {(ticket.date || info?.date) && (
              <p className='event-date'>{ticket.date || info.date}</p>
            )}
            {(ticket.address || info?.address) && (
              <p className='event-address'>{ticket.address || info.address}</p>
            )}
            {ticket.redeemed ? (
              <p className='error-text'>Heads up: this ticket has already been scanned at the door.</p>
            ) : (
              <p>Show this QR code at the door. It's good for one entry.</p>
            )}
            {ticket.qr && <img className='ticket-qr' src={ticket.qr} alt='Your ticket QR code' />}
            {ticket.email && <p className='help-text'>A copy was emailed to {ticket.email}.</p>}
          </div>
        ) : finalizing ? (
          <div className='card'>
            <h1>Finalizing your ticket…</h1>
            <p className='help-text'>One moment — confirming your payment.</p>
          </div>
        ) : info ? (
          <>
            <div className='card ticket-card'>
              <h1>{info.name}</h1>
              {info.date && <p className='event-date'>{info.date}</p>}
              {info.address && <p className='event-address'>{info.address}</p>}
              <p>Tap the poster to buy a ticket{info.priceDisplay ? ` — ${info.priceDisplay}` : ''}.</p>
              <button
                type='button'
                className='ticket-poster-btn'
                onClick={buy}
                disabled={starting || info.configured === false}
                aria-label={`Buy a ticket for ${info.name}`}
              >
                <img className='ticket-poster' src={info.poster} alt={`${info.name} event poster`} />
                <span className='ticket-poster-cta'>
                  {info.configured === false
                    ? 'Tickets not on sale yet'
                    : starting ? 'Opening checkout…' : 'Buy ticket'}
                </span>
              </button>
              {error && <p className='error-text'>{error}</p>}
            </div>

            {info.songRequests && (
              <form className='card' onSubmit={submitSongRequest}>
                <h2>Request a song for {info.name} to perform</h2>
                <p>
                  <strong>Comes with an admission ticket</strong> to {info.eventName || 'the show'} — plus a
                  free shot at the bar if you're 21+ (ID required).
                </p>
                <p className='help-text'>
                  The most-requested song gets played. Leave the song fields blank to just get your
                  entry + shot.
                </p>
                <div className='field'>
                  <label className='label' htmlFor='song-title'>Song title</label>
                  <input
                    id='song-title'
                    className='input'
                    type='text'
                    value={songForm.title}
                    maxLength={200}
                    onChange={(e) => setSongForm((f) => ({ ...f, title: e.target.value }))}
                  />
                </div>
                <div className='field'>
                  <label className='label' htmlFor='song-artist'>Original artist</label>
                  <input
                    id='song-artist'
                    className='input'
                    type='text'
                    value={songForm.originalArtist}
                    maxLength={200}
                    onChange={(e) => setSongForm((f) => ({ ...f, originalArtist: e.target.value }))}
                  />
                </div>
                <div className='field'>
                  <label className='label' htmlFor='song-link'>Link (optional)</label>
                  <input
                    id='song-link'
                    className='input'
                    type='url'
                    inputMode='url'
                    placeholder='YouTube, Spotify, …'
                    value={songForm.link}
                    maxLength={200}
                    onChange={(e) => setSongForm((f) => ({ ...f, link: e.target.value }))}
                  />
                </div>
                <button type='submit' className='btn btn-primary' disabled={submittingSong}>
                  {submittingSong
                    ? 'Opening checkout…'
                    : hasSongInput
                      ? `Request this song + get in${priceLabel}`
                      : `Get in + free shot (21+)${priceLabel}`}
                </button>
                {songError && <p className='error-text'>{songError}</p>}
              </form>
            )}
          </>
        ) : (
          <div className='card'>
            <h1>Tickets</h1>
            <p className='help-text'>{error || 'Loading…'}</p>
          </div>
        )}
        <SupportLine />
      </div>
      </main>
    </>
  );
}

export default TicketPage;
